<script>
import smtpNoReplyEmail from "../components/settings/setting-smtp-no-reply-email";
import personalInformation from "../components/settings/setting-personalInformation.vue";
import emailSignature from "../components/settings/setting-emailsignature.vue";
import resetPassword from "../components/settings/setting-resetPassword.vue";
import profilePreferences from "../components/settings/setting-profilePreferences.vue";
import settingEmailNotification from "../components/settings/setting-emailNotification.vue";

const SECTIONS = [
  {
    key: "my-info",
    label: "My Information",
    icon: "user",
  },
  {
    key: "email",
    label: "Email Settings",
    icon: "mail",
  },
  {
    key: "preferences",
    label: "Preferences",
    icon: "sliders",
  },
  {
    key: "password",
    label: "Password",
    icon: "lock",
  },
];

export default {
  components: {
    smtpNoReplyEmail,
    personalInformation,
    emailSignature,
    resetPassword,
    profilePreferences,
    settingEmailNotification,
  },
  data() {
    let dismissed = false;
    try {
      dismissed = window.localStorage.getItem("experience-banner::profile::dismissed") === "1";
    } catch (e) {
      // localStorage unavailable
    }
    const hash = (window.location.hash || "").replace("#", "");
    return {
      bannerDismissed: dismissed,
      sections: SECTIONS,
      activeSection: SECTIONS.some((s) => s.key === hash) ? hash : "my-info",
    };
  },
  async mounted() {
    await this.$A.UpdateCurrentSubscriptionState();
  },
  methods: {
    selectSection(key) {
      this.activeSection = key;
      try {
        window.history.replaceState(null, "", `#${key}`);
      } catch (e) {
        window.location.hash = key;
      }
    },
    dismissBanner() {
      this.bannerDismissed = true;
      try {
        window.localStorage.setItem("experience-banner::profile::dismissed", "1");
      } catch (e) {
        // localStorage unavailable
      }
    },
  },
};
</script>

<template>
  <div class="nav-margin profile-page">
    <navigation></navigation>

    <div class="page-body">
      <div class="experience-switch" v-if="!bannerDismissed">
        <div class="experience-msg">
          <span class="experience-pill">New</span>
          <span class="experience-text">You're using the new profile settings.</span>
        </div>
        <div class="experience-actions">
          <button type="button" class="experience-link experience-link-btn" v-b-modal="'feedbackModal-newProfile'">Give feedback</button>
        </div>
        <button
          type="button"
          class="experience-close"
          @click="dismissBanner"
          aria-label="Dismiss banner"
          title="Dismiss"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      <feedbackModal
        name="feedbackModal-newProfile"
        campaign-key="new-profile-v1"
        title="How are the new profile settings?"
        subtitle="Your take helps us decide what to improve."
        scale-question="How would you rate the new profile experience?"
        :scale-min="1"
        :scale-max="7"
        scale-low-label="Not great"
        scale-high-label="Excellent"
        text-prompt="What's working or not working?"
        text-placeholder="Tell us anything that helps."
        submit-label="Send feedback"
      />

      <div class="profile-shell">
        <aside class="settings-rail">
          <div class="rail-head">
            <div class="rail-head-title">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
              </svg>
              <span>Profile Settings</span>
            </div>
          </div>

          <nav class="rail-scroll" aria-label="Profile settings sections">
            <button
              v-for="section in sections"
              :key="section.key"
              type="button"
              class="rail-nav-item"
              :class="{ 'is-active': activeSection === section.key }"
              @click="selectSection(section.key)"
            >
              <svg v-if="section.icon === 'user'" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              <svg v-else-if="section.icon === 'mail'" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
              <svg v-else-if="section.icon === 'sliders'" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="4" y1="21" x2="4" y2="14" /><line x1="4" y1="10" x2="4" y2="3" />
                <line x1="12" y1="21" x2="12" y2="12" /><line x1="12" y1="8" x2="12" y2="3" />
                <line x1="20" y1="21" x2="20" y2="16" /><line x1="20" y1="12" x2="20" y2="3" />
                <line x1="1" y1="14" x2="7" y2="14" /><line x1="9" y1="8" x2="15" y2="8" /><line x1="17" y1="16" x2="23" y2="16" />
              </svg>
              <svg v-else width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              <span>{{ section.label }}</span>
            </button>
          </nav>
        </aside>

        <main class="profile-main">
          <div class="profile-main-inner">
            <div v-show="activeSection === 'my-info'" class="section-stack">
              <personalInformation />
            </div>
            <div v-show="activeSection === 'email'" class="section-stack">
              <settingEmailNotification />
              <emailSignature />
              <smtp-no-reply-email :is-account-settings="false" />
            </div>
            <div v-show="activeSection === 'preferences'" class="section-stack">
              <profilePreferences />
            </div>
            <div v-show="activeSection === 'password'" class="section-stack">
              <resetPassword />
            </div>
          </div>
        </main>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* The document scrolls naturally (promo banners etc. can add height
   above us); the rail stays pinned via position:sticky instead of a
   fixed-height shell, so there's a single page scrollbar. */
.page-body {
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 64px); /* clear position:fixed nav */
}

/* Experience banner */
.experience-switch {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  column-gap: 16px;
  row-gap: 4px;
  padding: 8px 48px;
  background: rgba(1, 146, 86, 0.06);
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  font-size: 13px;
  color: #4b5563;
  flex-shrink: 0;
}
.experience-msg,
.experience-actions {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.experience-pill {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  background: #019256;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  border-radius: 999px;
  letter-spacing: 0.02em;
}
.experience-link {
  color: #019256;
  font-weight: 600;
  text-decoration: none;
}
.experience-link:hover {
  text-decoration: underline;
  color: #017a48;
}
.experience-link-btn {
  background: transparent;
  border: 0;
  padding: 0;
  font: inherit;
  cursor: pointer;
}
.experience-close {
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: transparent;
  border: 0;
  border-radius: 6px;
  color: #6b7280;
  cursor: pointer;
  transition: background-color 120ms ease, color 120ms ease;
}
.experience-close:hover {
  background: rgba(15, 23, 42, 0.06);
  color: #111827;
}

/* Shell: sidebar + main column, like the proof list */
.profile-shell {
  display: flex;
  align-items: stretch;
  flex: 1;
  background: #f4f5f7;
  position: relative;
}
.settings-rail {
  width: 288px;
  flex-shrink: 0;
  align-self: flex-start;
  position: sticky;
  top: 64px; /* below the fixed nav */
  height: calc(100vh - 64px);
  display: flex;
  flex-direction: column;
  background: #fbfbfc;
  border-right: 1px solid rgba(15, 23, 42, 0.08);
  overflow: hidden;
}
.rail-head {
  flex-shrink: 0;
  height: 57px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 0 14px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
}
.rail-head-title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: 0.01em;
  white-space: nowrap;
}
.rail-head-title svg {
  color: #019256;
  flex-shrink: 0;
}
.rail-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 12px 10px 20px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  scrollbar-width: thin;
}
.rail-nav-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 9px 12px;
  background: transparent;
  border: 0;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #4b5563;
  text-align: left;
  cursor: pointer;
  font-family: inherit;
  transition: background-color 120ms ease, color 120ms ease;
}
.rail-nav-item svg {
  flex-shrink: 0;
  color: #9ca3af;
  transition: color 120ms ease;
}
.rail-nav-item:hover {
  background: rgba(15, 23, 42, 0.04);
  color: #111827;
}
.rail-nav-item.is-active {
  background: rgba(1, 146, 86, 0.08);
  color: #017a48;
  font-weight: 600;
}
.rail-nav-item.is-active svg {
  color: #019256;
}

/* Main column — scrolls with the document */
.profile-main {
  flex: 1;
  min-width: 0;
  background: #f4f5f7;
}
.profile-main-inner {
  max-width: 991px;
  margin: 0 auto;
  padding: 24px;
}
.section-stack {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* Mobile */
@media (max-width: 991px) {
  .profile-shell {
    flex-direction: column;
  }
  .settings-rail {
    width: 100%;
    position: static;
    height: auto;
    border-right: 0;
    border-bottom: 1px solid rgba(15, 23, 42, 0.08);
  }
  .rail-head {
    display: none;
  }
  .rail-scroll {
    flex-direction: row;
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    padding: 8px 12px;
  }
  .rail-nav-item {
    flex-shrink: 0;
    width: auto;
    white-space: nowrap;
  }
  .profile-main-inner {
    padding: 16px;
  }
}
</style>
